import { Button, DrawerBody, Empty, FCC, Icon, Id, Loading, Separator, TextControl, useDebounce } from "@dgs/core";
import { useQuery } from "@tanstack/react-query";
import { FC, ReactNode, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { travelSegmentService } from "../../../../shared/api/travelSegmentService";
import { TravelSectionSettingsResource } from "../../../../shared/types";
import {
	SelectableSegmentType,
	SelectableTravelSegmentIndexResource,
	TravelDirection,
	TravelSegmentFormValues,
	TravelSegmentIndexResource,
	TravelSegmentType,
} from "../../../../shared/types/travelSegment";
import { useRegistrationForm } from "../../../provider/registrationFormProviderContext";
import { travelSegmentIdentifier } from "../travelSegmentIdentifier";
import { SelectableTravelSegmentCard } from "./SelectableTravelSegmentCard";
import { SelectableTravelSegmentSelectionCard } from "./SelectableTravelSegmentSelectionCard";
import {
	TravelSegmentDrawerEmptyList,
	TravelSegmentDrawerList,
	TravelSegmentDrawerListItem,
} from "./travelSegmentDrawerComponents";
import { TravelSegmentTypeFilterBar } from "./TravelSegmentTypeFilterBar";

interface ListProps {
	direction: TravelDirection;
	onSelect: (segment: SelectableTravelSegmentIndexResource | TravelSegmentFormValues) => void;
	availableTypes: SelectableSegmentType[];
	initialSelection: (TravelSegmentIndexResource | TravelSegmentFormValues)[];
	selection: (SelectableTravelSegmentIndexResource | TravelSegmentFormValues)[];
	registrationFormId: Id;
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.spacing(4)};

	> * {
		padding-bottom: 0;
	}
`;
const SelectionList = styled(TravelSegmentDrawerList)`
	max-height: 33vh;
	overflow-y: auto;
`;

const TravelSegmentList: FCC<ListProps> = ({
	direction,
	availableTypes,
	onSelect,
	initialSelection,
	selection,
	registrationFormId,
	children,
}) => {
	const { t } = useTranslation();
	const { registrationForm } = useRegistrationForm();
	const [filterByType, setFilterByType] = useState<TravelSegmentType | null>(null);
	const [searchBy, setSearchBy] = useState("");
	const debouncedSearchBy = useDebounce(searchBy, 500);
	const { data: travelSegments, isLoading } = useQuery({
		queryKey: travelSegmentService.travelSegmentByDirectionKey(registrationFormId, direction, {
			search: debouncedSearchBy,
			type: filterByType,
			guestId: registrationForm.guest.id === "new" ? null : registrationForm.guest.id,
			withPublicScope: true,
		}),
		queryFn: async () => {
			const response = await travelSegmentService.travelSegmentByDirection(registrationFormId, direction, {
				search: debouncedSearchBy,
				type: filterByType,
				guestId: registrationForm.guest.id === "new" ? null : registrationForm.guest.id,
				withPublicScope: true,
			});

			return response.data.data;
		},
	});

	const visibleTravelSegments = useMemo(() => {
		if (!travelSegments) {
			return travelSegments;
		}

		return travelSegments.filter(({ id }) => {
			return !initialSelection.some((initialSegment) => initialSegment.id === id);
		});
	}, [travelSegments, initialSelection]);

	return (
		<Container>
			<TravelSegmentTypeFilterBar
				activeType={filterByType}
				onChange={setFilterByType}
				availableTypes={availableTypes}
			/>
			<TextControl
				placeholder={t("Search")}
				name="search"
				value={searchBy}
				prefix={<Icon $icon="search" />}
				onChange={(e) => setSearchBy(e.target.value)}
			/>
			<Separator />
			<TravelSegmentDrawerList aria-label={t("Travel segments")}>
				{!visibleTravelSegments && isLoading && (
					<TravelSegmentDrawerListItem>
						<Loading />
					</TravelSegmentDrawerListItem>
				)}
				{visibleTravelSegments && visibleTravelSegments.length === 0 && (
					<TravelSegmentDrawerEmptyList>
						<Empty>{t("No travel segments found.")}</Empty>
					</TravelSegmentDrawerEmptyList>
				)}
				{visibleTravelSegments &&
					visibleTravelSegments.map((segment) => (
						<TravelSegmentDrawerListItem
							key={segment.id}
							aria-label={`${t(segment.type)} ${travelSegmentIdentifier(segment)}`}
							$isSelectable={!initialSelection.some(({ id }) => id === segment.id)}
							onClick={() =>
								!(segment.contingent && segment.travelCount && segment.contingent <= segment.travelCount)
									? onSelect(segment)
									: undefined
							}
						>
							<SelectableTravelSegmentCard
								travelSegment={segment}
								isActive={selection.some(({ id }) => id === segment.id)}
								direction={direction}
							/>
						</TravelSegmentDrawerListItem>
					))}
				{children}
			</TravelSegmentDrawerList>
		</Container>
	);
};

interface Props {
	registrationFormId: Id;
	availableTypes: SelectableSegmentType[];
	direction: TravelDirection;
	initialSelection: (TravelSegmentIndexResource | TravelSegmentFormValues)[];
	onSave: (selection: (SelectableTravelSegmentIndexResource | TravelSegmentFormValues)[]) => void;
	settings: TravelSectionSettingsResource;
	children: (
		addToSelection: (segment: SelectableTravelSegmentIndexResource | TravelSegmentFormValues) => void,
	) => ReactNode;
}

const WideDrawerBody = styled(DrawerBody)`
	flex: 1;
`;

const WideDrawerFooter = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.spacing(2)};
	box-shadow: ${({ theme }) => theme.shadows.l2};
	padding: ${({ theme }) => theme.spacing(4)};
`;

const WideDrawerFooterAction = styled.div`
	display: flex;
	justify-content: flex-end;
	margin-top: ${({ theme }) => theme.spacing(1)};
`;

export const AddTravelSegmentDrawerContent: FC<Props> = ({
	direction,
	availableTypes,
	initialSelection,
	onSave,
	registrationFormId,
	children,
}) => {
	const { t } = useTranslation();
	const [selection, setSelection] = useState<(SelectableTravelSegmentIndexResource | TravelSegmentFormValues)[]>([]);
	const handleSelect = useCallback((segment: SelectableTravelSegmentIndexResource | TravelSegmentFormValues) => {
		setSelection((current) => {
			if (current.some(({ id }) => segment.id === id)) {
				return current.filter(({ id }) => segment.id !== id);
			}

			return [...current, segment].sort((a, b) =>
				`${a.arrivalDate} ${a.arrivalTime}`.localeCompare(`${b.arrivalDate} ${b.arrivalTime}`),
			);
		});
	}, []);

	const handleRemoveFromSelection = useCallback(
		(segment: SelectableTravelSegmentIndexResource | TravelSegmentFormValues) => {
			setSelection((current) => current.filter(({ id }) => segment.id !== id));
		},
		[],
	);

	return (
		<>
			<WideDrawerBody>
				<TravelSegmentList
					registrationFormId={registrationFormId}
					direction={direction}
					availableTypes={availableTypes}
					onSelect={handleSelect}
					selection={selection}
					initialSelection={initialSelection}
				>
					{children(handleSelect)}
				</TravelSegmentList>
			</WideDrawerBody>
			{selection.length > 0 && (
				<WideDrawerFooter>
					<SelectionList aria-label={t("Selected travel segments")}>
						{selection.map((segment, index) => (
							<TravelSegmentDrawerListItem
								key={index}
								aria-label={`${t(segment.type)} ${travelSegmentIdentifier(segment)}`}
							>
								<SelectableTravelSegmentSelectionCard
									travelSegment={segment}
									onDelete={() => handleRemoveFromSelection(segment)}
								/>
							</TravelSegmentDrawerListItem>
						))}
					</SelectionList>
					<WideDrawerFooterAction>
						<Button type="button" color="primary" onClick={() => onSave(selection)}>
							{t("Save")}
						</Button>
					</WideDrawerFooterAction>
				</WideDrawerFooter>
			)}
		</>
	);
};
